/**
 * Whether the Product Information form is safe to show and to submit.
 *
 * The screen posts `gradingGroups` and `sizeRanges` while offering no control for either — it
 * round-trips whatever the read handed it, and the server writes back what it receives. An
 * editable form standing in front of a product that never loaded therefore submits the initial
 * empty state over stored grading data, and reports success while doing it.
 *
 * The previous guard released as soon as the detail request *settled*, which a failed request
 * also does, so a failure rendered an empty, fully editable form. Failure is now its own phase.
 */
export type ProductLoadPhase = 'ready' | 'loading' | 'failed';

export interface ProductLoadSignals {
  /** The screen is editing or duplicating a saved product, so it must load one first. */
  needsProduct: boolean;
  /** The detail request resolved with a product. */
  productLoaded: boolean;
  /** The detail request finished without producing a product. */
  productLoadFailed: boolean;
  /** The fetched product has been applied to the form state. */
  prefillApplied: boolean;
}

/**
 * Resolves which of the three phases the screen is in.
 * @param signals What the load has reported so far.
 * @returns `ready` only when the form owns the product it is about to overwrite.
 */
export const resolveProductLoadPhase = (signals: ProductLoadSignals): ProductLoadPhase => {
  if (!signals.needsProduct) {
    return 'ready';
  }
  if (signals.productLoadFailed) {
    return 'failed';
  }
  if (!signals.productLoaded || !signals.prefillApplied) {
    return 'loading';
  }
  return 'ready';
};

/**
 * Whether a save may be issued from the current phase.
 * @param phase The resolved load phase.
 * @returns True only in `ready`, so neither a pending nor a failed load can submit.
 */
export const canSubmitProduct = (phase: ProductLoadPhase): boolean => phase === 'ready';
